import {FullscreenControl, GeoJSONSourceRaw, Popup} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as React from "react";
import {useCallback, useMemo} from "react";
import ReactMapboxGl, {Layer, ScaleControl, Source} from "react-mapbox-gl";
import {MapEvent} from "react-mapbox-gl/lib/map-events";
import {apiMapboxAccessToken} from "../../../api/configuration";
import {toGeoJSON} from "@mapbox/polyline";
import {makeStyles} from "@material-ui/core";
import {centerMedian} from "@turf/turf";
import {FitBounds} from "react-mapbox-gl/lib/map";
import {MarkerPopup} from "../HubMarker/HubMarkerPopup";
import operatorActiveHubIcon from "components/src/Icons/Hub/operator-active-hub.svg";
import operatorInactiveHubIcon from "components/src/Icons/Hub/operator-inactive-hub.svg";
import carrierActiveHubIcon from "components/src/Icons/Hub/carrier-active-hub.svg";
import carrierInactiveHubIcon from "components/src/Icons/Hub/carrier-inactive-hub.svg";
import {useCycleLanesToggleButton} from "./useCycleLanesToggleButton";
import { MapScaleControl } from "components/src/Component/MapScaleControl";

const styles = {
  londonCycle: "mapbox://styles/mapbox/light-v9",
  light: "mapbox://styles/mapbox/light-v9",
  dark: "mapbox://styles/mapbox/dark-v10",
  basic: "mapbox://styles/mapbox/basic-v9",
  outdoor: "mapbox://styles/mapbox/outdoors-v10",
  street: "mapbox://styles/mapbox/streets-v11",
};

const Map = ReactMapboxGl({
  accessToken: apiMapboxAccessToken,
});

const mapStyle = {
  width: "100%",
  height: "600px",
  marginLeft: "30px",
};

const clickPadding = 20 // in px

const useStyles = makeStyles(() => ({
  popup: {
    '& .mapboxgl-popup-content': {
      background: 'none',
      color: 'rgb(236,222,239)',
      backgroundColor: 'rgba(14,28,11,0.8)',
      padding: '1px 6px',
      margin: '2px',
      boxSizing: 'border-box',
      borderRadius: '10%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    '& .mapboxgl-popup-tip': {
      backgroundColor: 'rgba(236,222,239,0.2)',
      border: '1px solid rgba(236,222,239,0.8)',
      boxSizing: 'border-box',
      width: `${clickPadding * 2}px`,
      height: `${clickPadding * 2}px`,
      padding: 0,
    }
  },
}))

interface FluxmapProps {
  onStyleLoad?: (map: any) => any;
  polylines: string[]
  hubFeatures: GeoJSON.Feature<GeoJSON.Geometry>[]
}

export const Fluxmap = (props: FluxmapProps) => {
  const { polylines, hubFeatures } = props;
  const classes = useStyles()

  const polylineGeoJsonSource: GeoJSONSourceRaw = useMemo(() => {
    let index = 0
    const source: GeoJSONSourceRaw = {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: polylines.map((polyline) => {
          const feature: GeoJSON.Feature<GeoJSON.Geometry> = {
            id: index,
            type: "Feature",
            geometry: toGeoJSON(polyline) as GeoJSON.Geometry,
            properties: []
          }
          index ++
          return feature
        })
      }
    }
    return source
  }, [polylines]);

  const mapBounds: FitBounds = useMemo(() => {
    if (polylines.length > 0) {
      const center = centerMedian(polylineGeoJsonSource.data).geometry.coordinates
      const offset = 0.05
      return [[center[0] - offset, center[1] - offset], [center[0] + offset, center[1] + offset]]
    } else {
      return [[-12.973238, 40.968836], [18.667387, 51.183932]]
    }
  }, [polylineGeoJsonSource.data, polylines.length]);

  const layerPaint: any = useMemo(() => {
    return {
      'line-color': '#f5b042',
      'line-width': 1.2,
      'line-opacity': (1 / (polylines.length / 200 + 1)),
    }
  }, [polylines.length]);

  const [cycleLanesVisible, handleCycleLanesButtonLoad] = useCycleLanesToggleButton(false)

  const handleStyleLoad: MapEvent = useCallback((map: mapboxgl.Map) => {
    const { onStyleLoad } = props;
    map.addControl(new FullscreenControl());
    map.on('click', (e) => {
      const bbox = [
        [e.point.x - clickPadding, e.point.y - clickPadding],
        [e.point.x + clickPadding, e.point.y + clickPadding]
      ]

      const features = map.queryRenderedFeatures(
          bbox,
          { layers: ['polylines'] }
      ).filter((value, index, self) =>
          self.findIndex(v => v.id === value.id) === index
      )

      // Display a tooltip with the count of intersecting polylines
      new Popup({ closeOnMove: true, closeButton: false, className: classes.popup })
          .setLngLat(e.lngLat)
          .setText(`${features.length} passage${features.length > 1 ? 's' : ''}`)
          .setOffset(-clickPadding)
          .addTo(map)
    })

    map.addImage("operator-active-hub", operatorActiveHubImg)
    map.addImage("operator-inactive-hub", operatorInactiveHubImg)
    map.addImage("carrier-active-hub", carrierActiveHubImg)
    map.addImage("carrier-inactive-hub", carrierInactiveHubImg)

    // displayed scale is fucked up on low zoom levels
    map.setMinZoom(4)

    handleCycleLanesButtonLoad(map)

    return onStyleLoad && onStyleLoad(map);
  }, [props, classes.popup, handleCycleLanesButtonLoad]);

  const operatorActiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const operatorInactiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const carrierActiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const carrierInactiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const parsedPickupPoints: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};

  hubFeatures?.forEach((feature: GeoJSON.Feature<GeoJSON.Geometry>) => {
    const type = feature?.properties?.hubType ?? null
    switch (type) {
      case "OPERATOR_ACTIVE_HUB":
        operatorActiveHubs.features.push(feature)
        break;
      case "OPERATOR_INACTIVE_HUB":
        operatorInactiveHubs.features.push(feature)
        break;
      case "CARRIER_ACTIVE_HUB":
        carrierActiveHubs.features.push(feature)
        break;
      case "CARRIER_INACTIVE_HUB":
        carrierInactiveHubs.features.push(feature)
        break;
      default:
        parsedPickupPoints.features.push(feature)
        break;
    }
  });

  const operatorActiveHubsSource: GeoJSONSourceRaw = {
    data: operatorActiveHubs,
    type: "geojson",
  };

  const operatorInactiveHubsSource: GeoJSONSourceRaw = {
    data: operatorInactiveHubs,
    type: "geojson",
  };

  const carrierActiveHubsSource: GeoJSONSourceRaw = {
    data: carrierActiveHubs,
    type: "geojson",
  };

  const carrierInactiveHubsSource: GeoJSONSourceRaw = {
    data: carrierInactiveHubs,
    type: "geojson",
  };

  const operatorActiveHubImg = new Image(25, 25);
  operatorActiveHubImg.src = operatorActiveHubIcon;

  const operatorInactiveHubImg = new Image(25, 25);
  operatorInactiveHubImg.src = operatorInactiveHubIcon;

  const carrierActiveHubImg = new Image(25, 25);
  carrierActiveHubImg.src = carrierActiveHubIcon;

  const carrierInactiveHubImg = new Image(25, 25);
  carrierInactiveHubImg.src = carrierInactiveHubIcon;

  return (
      <Map
          style={styles.dark}
          containerStyle={mapStyle}
          onStyleLoad={handleStyleLoad}
          fitBounds={mapBounds}
      >
        <Source id="cycle-lanes" tileJsonSource={{ type: "vector", url: "mapbox://colisactiv-tech.0ygnovo4" }} />
        <Layer
            id="cycle-lanes-layer"
            type="line"
            sourceId="cycle-lanes"
            sourceLayer="france-20250801-030x0s"
            layout={{ visibility: cycleLanesVisible ? 'visible' : 'none' }}
            paint={{
              "line-color": "#5B9E94",
              "line-opacity": 1,
              "line-width": 5
            }}
        />
        <Source id="polylines" geoJsonSource={polylineGeoJsonSource} />
        <Layer
            type="line"
            id="polylines"
            sourceId="polylines"
            paint={layerPaint}
        />
        <Source id="operator-active-hub" geoJsonSource={operatorActiveHubsSource} />
        <Layer
            id="operator-active-hub-marker"
            type="symbol"
            layout={{ "icon-image": "operator-active-hub" }}
            sourceId="operator-active-hub"
        />
        <Source id="operator-inactive-hub" geoJsonSource={operatorInactiveHubsSource} />
        <Layer
            id="operator-inactive-hub-marker"
            type="symbol"
            layout={{ "icon-image": "operator-inactive-hub" }}
            sourceId="operator-inactive-hub"
        />
        <Source id="carrier-active-hub" geoJsonSource={carrierActiveHubsSource} />
        <Layer
            id="carrier-active-hub-marker"
            type="symbol"
            layout={{ "icon-image": "carrier-active-hub" }}
            sourceId="carrier-active-hub"
        />
        <Source id="carrier-inactive-hub" geoJsonSource={carrierInactiveHubsSource} />
        <Layer
            id="carrier-inactive-hub-marker"
            type="symbol"
            layout={{ "icon-image": "carrier-inactive-hub" }}
            sourceId="carrier-inactive-hub"
        />
        {<MarkerPopup />}
        <MapScaleControl darkMode />
      </Map>
  );
};
